import React, { useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Clear } from '@material-ui/icons';
import { keyBy } from 'lodash';
import { emptySelectValue, TP } from '../../constants/consts';
import { getCitiesAction, getList } from '../App/actions';
import { COUNTRY_CITY_ENDPOINT } from '../../constants/endpointConstants';
import { getOption } from '../../utils/common';
import { borderRadius, colors, fontSize } from '../../constants/styleConstants';

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    height: 45,
    marginBottom: 2,
  },
  input: {
    padding: '8px 10px',
    border: `1px solid ${colors.fieldsBorderGray} !important`,
    borderRadius: borderRadius.fields,
    backgroundColor: colors.white,
    '&:focus': {
      outline: 'none',
    },
    position: 'absolute',
    top: 0,
    left: 0,
    width: '99%',
    bottom: 0,
  },
  close: {
    fontSize: '1rem',
    position: 'absolute',
    right: 15,
    top: '50%',
    marginTop: -8,
    color: colors.primary,
    cursor: 'pointer',
  },
  label: {
    color: colors.title,
    fontSize: fontSize.smallTitle,
  },
}));

const GoogleAddressField = ({ setFieldValue, countryField, cityField, countriesOptions, field, label }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const inputEl = useRef(null);

  let autocomplete;
  const componentForm = {
    country: { name: 'country', value: 'long_name' },
    locality: { name: 'city', value: 'long_name' },
    administrative_area_level_1: { name: 'region', value: 'short_name' },
    route: { name: 'street', value: 'long_name' },
    street_number: { name: 'building', value: 'short_name' },
    postal_code: { name: 'zip', value: 'short_name' },
  };

  window.initMap = function() {
    if (inputEl.current && countriesOptions.length) {
      initAutocomplete(inputEl.current);
    }
  };

  React.useEffect(() => {
    if (!window.google?.maps?.places && process.env.GOOGLE_MAPS_KEY) {
      const head = document.getElementsByTagName('head').item(0);
      const script = document.createElement('script');
      script.setAttribute(
        'src',
        `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_MAPS_KEY}&libraries=places&language=en&callback=initMap`,
      );
      head.appendChild(script);
    } else {
      const { initMap } = window;
      initMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const { initMap } = window;
    initMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputEl.current, countriesOptions]);

  const initAutocomplete = input => {
    const { google } = window;
    if (google) {
      autocomplete = new google.maps.places.Autocomplete(input, { types: ['geocode'] });
      autocomplete.setFields(['address_component']);
      autocomplete.addListener('place_changed', fillInAddress);
    }
  };

  const getCities = (value, countryId) => {
    dispatch(
      getList({
        endpoint: COUNTRY_CITY_ENDPOINT(countryId),
        sagaRoutine: getCitiesAction,
        fieldName: cityField.name,
        params: {
          queryParams: { query: value },
        },
        callback: data => {
          const cityOption = data.data.find(el => el.name === value);
          setFieldValue(cityField.name, cityOption ? getOption(cityOption) : '');
        },
      }),
    );
  };

  const fillInAddress = () => {
    const place = autocomplete.getPlace();
    const pl = autocomplete.gm_accessors_.place;
    const placeString = pl[Object.keys(pl).find(el => pl[el].formattedPrediction)]?.formattedPrediction;
    let countryValue;
    let cityValue;
    const addressComponents = keyBy(place.address_components, 'types.0');
    Object.keys(componentForm).forEach(component => {
      const val = addressComponents[component] ? addressComponents[component][componentForm[component].value] : '';
      if (component === 'country') {
        countryValue = val;
      } else if (component === 'locality') {
        cityValue = val;
      }
    });
    setFieldValue(field.name, placeString);
    const countryOption = countriesOptions.find(el => el.name === countryValue);
    setFieldValue(countryField.name, countryOption || '');
    if (countryOption?.value) {
      getCities(cityValue, countryOption.value);
    } else {
      setFieldValue(cityField.name, '');
    }
  };

  const clearAddress = () => {
    setFieldValue(field.name, '');
  };

  return (
    <>
      <span className={classes.label}>{label}</span>
      <div className={classes.root}>
        <input
          ref={inputEl}
          className={classes.input}
          type="text"
          name={field.name}
          value={field.value}
          onChange={val => {
            field.onChange(val);
          }}
        />
        {field.value && <Clear className={classes.close} onClick={clearAddress} />}
      </div>
    </>
  );
};

GoogleAddressField.propTypes = {
  countryField: PropTypes.object,
  cityField: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  countriesOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  field: PropTypes.shape({ name: PropTypes.string, value: PropTypes.string, onChange: PropTypes.func }).isRequired,
  label: PropTypes.string,
};

GoogleAddressField.defaultProps = {
  countryField: { name: 'country', label: `${TP}.m_COUNTRY` },
  cityField: { name: 'city', label: `${TP}.m_CITY` },
  countriesOptions: [emptySelectValue],
};

export default GoogleAddressField;
